'use client';

import React, {useState, useEffect} from 'react';
import {Button} from "@/shared/components/ui/button";
import {sendConsultationRequest} from "@/shared/lib/booking-api";

interface CarFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    showCarFields?: boolean;
    title: string;
}

interface FormData {
    name: string;
    phone: string;
    carBrand: string;
    carModel: string;
}

export default function CarFormModal({
                                         isOpen,
                                         onClose,
                                         showCarFields = true,
                                         title,
                                     }: CarFormModalProps) {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        phone: '',
        carBrand: '',
        carModel: '',
    });

    const [isMobile, setIsMobile] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const getInputNumbersValue = (input: HTMLInputElement) => {
        return input.value.replace(/\D/g, '');
    }

    const onPhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        let input = e.target as HTMLInputElement,
            inputNumbersValue = getInputNumbersValue(input);
        let pasted = e.clipboardData;
        if (pasted) {
            let pastedText = pasted.getData('Text');
            if (/\D/g.test(pastedText)) {
                input.value = inputNumbersValue;
                return;
            }
        }
    }

    const onPhoneInput = (e: React.FormEvent<HTMLInputElement>) => {
        let input = e.target as HTMLInputElement,
            inputNumbersValue = getInputNumbersValue(input),
            selectionStart = input.selectionStart || 0,
            formattedInputValue = "";

        if (!inputNumbersValue) {
            input.value = "";
            return;
        }

        if (input.value.length != selectionStart) {
            const event = e.nativeEvent as InputEvent;
            if (event.data && /\D/g.test(event.data)) {
                input.value = inputNumbersValue;
            }
            return;
        }

        if (["7", "8", "9"].indexOf(inputNumbersValue[0]) > -1) {
            if (inputNumbersValue[0] == "9") inputNumbersValue = "7" + inputNumbersValue;
            let firstSymbols  = (inputNumbersValue[0] == "8") ? "8" : "+7";
            formattedInputValue = input.value = firstSymbols + " ";
            if (inputNumbersValue.length > 1) {
                formattedInputValue += '(' + inputNumbersValue.substring(1, 4);
            }
            if (inputNumbersValue.length >= 5) {
                formattedInputValue += ') ' + inputNumbersValue.substring(4, 7);
            }
            if (inputNumbersValue.length >= 8) {
                formattedInputValue += '-' + inputNumbersValue.substring(7, 9);
            }
            if (inputNumbersValue.length >= 10) {
                formattedInputValue += '-' + inputNumbersValue.substring(9, 11);
            }
        } else {
            formattedInputValue = '+' + inputNumbersValue.substring(0, 16);
        }
        input.value = formattedInputValue;
        setFormData(prev => ({ ...prev, phone: formattedInputValue }));
    }

    const onPhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        let input = e.target as HTMLInputElement;
        let inputValue = input.value.replace(/\D/g, '');
        if (e.key === 'Backspace' && inputValue.length === 1) {
            input.value = "";
            setFormData(prev => ({ ...prev, phone: '' }));
        }
    }

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);

    // Блокировка скролла при открытии модалки
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            // Сброс формы при открытии
            setFormData({
                name: '',
                phone: '',
                carBrand: '',
                carModel: '',
            });
            setIsSubmitting(false);
            setSubmitSuccess(false);
            setPrivacyAgreed(false);
            setShowSuccessModal(false);
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const validatePhone = (phone: string): string | null => {
        const phoneDigits = phone.replace(/\D/g, '');
        if (!phone || phoneDigits.length < 11) {
            return 'Введите корректный номер телефона';
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!privacyAgreed) {
            return;
        }
        
        // Валидация телефона
        const phoneError = validatePhone(formData.phone);
        if (phoneError) {
            alert(phoneError);
            return;
        }
        
        // Валидация имени
        if (!formData.name.trim()) {
            alert('Введите ваше имя');
            return;
        }
        
        setIsSubmitting(true);
        setSubmitSuccess(false);
        
        const result = await sendConsultationRequest({
            phone: formData.phone,
            name: formData.name.trim(),
            requestType: title,
            carBrand: formData.carBrand.trim() || undefined,
            carModel: formData.carModel.trim() || undefined,
        });
        
        if (result.success) {
            setSubmitSuccess(true);
            setShowSuccessModal(true);
            if (title == "Запись на экспертную консультацию") {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({'event':'form_consultation'});
            }
            if (title == "Заказать обратный звонок") {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({'event':'form_callback'});
            }
        } else {
            alert(result.error || 'Произошла ошибка при отправке формы. Пожалуйста, попробуйте еще раз.');
        }
        
        setIsSubmitting(false);
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={handleOverlayClick}>
            <div className={`bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col`}>
                {/* Фиксированный заголовок */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {title}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold"
                    >
                        ×
                    </button>
                </div>

                {/* Прокручиваемая форма */}
                <div className="overflow-y-auto flex-1">
                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Имя */}
                    <div>
                        <label
                            htmlFor="name"
                            className="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Имя *
                        </label>
                        <input name="name"
                               id="name"
                               value={formData.name}
                               onChange={handleInputChange}
                               required
                               className={'border-1 border-custom-ig w-full px-3 py-2 rounded-[5px] text-[18px] leading-[1.1] focus:border-custom-light-orange outline-hidden'}
                               placeholder={'Ваше имя'}
                        />
                    </div>

                    {/* Телефон */}
                    <div>
                        <label
                            htmlFor="phone"
                            className="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Телефон *
                        </label>
                        <input type="tel"
                               id="phone"
                               name="phone"
                               value={formData.phone}
                               onChange={(e) => onPhoneInput(e)}
                               onPaste={onPhonePaste}
                               onKeyDown={onPhoneKeyDown}
                               onFocus={(e) => {
                                   if (!e.target.value) {
                                       e.target.value = '+7 ';
                                       setFormData(prev => ({ ...prev, phone: '+7 ' }));
                                   }
                               }}
                               required
                               className={'border-1 border-custom-ig w-full px-3 py-2 rounded-[5px] text-[18px] leading-[1.1] focus:border-custom-light-orange outline-hidden'}
                               placeholder={"+7 (XXX) XXX-XX-XX"}
                        />
                    </div>

                    {/* Марка автомобиля - опционально */}
                    {showCarFields && (
                        <>
                            <div>
                                <label
                                    htmlFor="carBrand"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Марка автомобиля
                                </label>
                                <input
                                    type="text"
                                    id="carBrand"
                                    name="carBrand"
                                    value={formData.carBrand}
                                    onChange={handleInputChange}
                                    className="border-1 border-custom-ig w-full px-3 py-2 rounded-[5px] text-[18px] leading-[1.1] focus:border-custom-light-orange outline-hidden"
                                    placeholder="Например: Toyota"
                                />
                            </div>

                            {/* Модель автомобиля - опционально */}
                            <div>
                                <label
                                    htmlFor="carModel"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Модель автомобиля
                                </label>
                                <input
                                    type="text"
                                    id="carModel"
                                    name="carModel"
                                    value={formData.carModel}
                                    onChange={handleInputChange}
                                    className="border-1 border-custom-ig w-full px-3 py-2 rounded-[5px] text-[18px] leading-[1.1] focus:border-custom-light-orange outline-hidden"
                                    placeholder="Например: Camry"
                                />
                            </div>
                        </>
                    )}

                    {/* Чекбокс согласия с политикой */}
                    <div>
                        <label className="flex items-start cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={privacyAgreed}
                                onChange={(e) => setPrivacyAgreed(e.target.checked)}
                                required
                                className="mr-3 mt-1 w-4 h-4 lg:w-5 lg:h-5 cursor-pointer accent-custom-gray border-custom-light rounded focus:ring-custom-gray hover:scale-110 transition-transform flex-shrink-0"
                            />
                            <span className="text-[14px] lg:text-[16px] text-gray-700 leading-[1.3]">
                                Я согласен с{' '}
                                <a
                                    href="/privacy-policy.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-custom-orange hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    политикой обработки персональных данных
                                </a>
                            </span>
                        </label>
                    </div>

                    {/* Кнопки */}
                    <div className="flex gap-3 pt-4">
                        <Button variant={'filledOrange'} onClick={onClose}
                                className={'flex-1 px-4 py-2 lg:text-sm font-button leading-none'}
                                disabled={isSubmitting}>
                            Отмена
                        </Button>
                        <Button variant={'filledOrange'} type={"submit"}
                                className={'flex-1 px-4 py-2 lg:text-sm font-button leading-none'}
                                disabled={isSubmitting}>
                            {isSubmitting ? 'Отправка...' : submitSuccess ? 'Отправлено!' : 'отправить заявку'}
                        </Button>
                    </div>
                    
                </form>
                </div>
            </div>

            {/* Модальное окно успеха */}
            {showSuccessModal && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70"
                    onClick={() => setShowSuccessModal(false)}
                >
                    <div 
                        className="bg-white rounded-[20px] shadow-xl w-full max-w-[500px] p-8 text-center relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => {
                                setShowSuccessModal(false);
                                onClose();
                            }}
                            className="absolute top-4 right-4 text-custom-black hover:opacity-80 text-2xl"
                            style={{ fontSize: '28px', lineHeight: '1' }}
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                        
                        <h3 
                            className="text-[18px] sm:text-[20px] font-medium text-custom-black mb-4"
                        >
                            Заявка на запись сформирована!
                        </h3>
                        
                        <p 
                            className="text-[18px] sm:text-[20px] font-medium text-custom-black leading-relaxed"
                        >
                            Мы свяжемся с Вами для уточнения времени!
                        </p>
                        
                        <p 
                            className="text-[18px] sm:text-[20px] font-medium text-custom-black mb-6"
                        >
                            Спасибо!
                        </p>
                        
                        <button
                            onClick={() => {
                                setShowSuccessModal(false);
                                onClose();
                            }}
                            className="w-full py-3 px-4 text-[18px] font-normal tracking-[-0.02em] bg-custom-orange text-white rounded-[50px] transition-all duration-200 hover:bg-custom-orange/90"
                            style={{ fontFamily: 'var(--font-gothampro, sans-serif)' }}
                        >
                            Закрыть
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
